import { TranslateService } from '@ngx-translate/core';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Message, SpinnerService, UtilsService } from 'shared-components';
import { AdminContextService } from '../../context/admin-contex.service';
import { AdminService } from '../../services/admin.service';
import { SessionContextService } from 'projects/packcert-web/src/app/context/session-context.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-logs',
    templateUrl: './logs.component.html'
})
export class LogsComponent implements OnInit, OnDestroy {

    private _filters;
    public filtersForm: UntypedFormGroup;
    public filtersVisible = false;

    public logs;
    public roles;
    public tiposLogs;

    public mensajes = new Array<Message>();
    public showError: boolean;

    public dropdownSection: number;
    public pestanaActiva = new Array<number>();

    private readonly _DEFAULT_ITEMS_PER_PAGE = 10;
    public itemsPerPage = this._DEFAULT_ITEMS_PER_PAGE;
    public currentPage = 0;
    public totalItems: number;
    public readonly visiblePages = 3;

    public toCloseSpinner = 0;

    public orderBy: string;
    public columnId: number;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private _fb: UntypedFormBuilder,
        private readonly _spinnerService: SpinnerService,
        private readonly _sessionContext: SessionContextService,
        private readonly _adminContext: AdminContextService,
        private readonly _adminService: AdminService,
        private readonly utilsService: UtilsService,
        private readonly _translate: TranslateService
    ) { }

    ngOnInit() {
        this.filtersForm = this._fb.group({
            fechaInicio: [''],
            fechaFin: [''],
            userName: [''],
            applicationRoleId: [''],
            origen: [''],
            tipoLogId: ['']
        });

        this.filtersForm.get('fechaInicio').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());
        this.filtersForm.get('fechaFin').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());

        this._adminContext.logsFilters = null;

        this.getLogs();
        this.getRoles();
        this.getTiposLog();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get defaultLanguage() {
        return this._translate.getDefaultLang();
    }

    public get isSmallScreen() {
        return this._sessionContext.isSmallScreen();
    }

    public validDates() {
        if (this.filtersForm.get('fechaInicio').value && this.filtersForm.get('fechaFin').value) {
            if (this.checkDates(this.filtersForm.get('fechaInicio').value, this.filtersForm.get('fechaFin').value)) {
                this.clearDatesValidators();
            } else {
                this.filtersForm.get('fechaInicio').setErrors(['Invalid date']);
                this.filtersForm.get('fechaFin').setErrors(['Invalid date']);
            }
            if (this.filtersForm.get('fechaInicio').invalid || this.filtersForm.get('fechaFin').invalid) {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.fecha.error.finAnteriorInicio' });
            } else {
                this.showError = false;
                this.mensajes = [];
            }
        } else {
            this.clearDatesValidators();
            this.showError = false;
            this.mensajes = [];
        }
    }

    private clearDatesValidators() {
        if (this.filtersForm.get('fechaFin').errors && this.filtersForm.get('fechaFin').value) {
            this.filtersForm.get('fechaFin').clearValidators();
            this.filtersForm.get('fechaFin').updateValueAndValidity();
        }
        if (this.filtersForm.get('fechaInicio').errors && this.filtersForm.get('fechaInicio').value) {
            this.filtersForm.get('fechaInicio').clearValidators();
            this.filtersForm.get('fechaInicio').updateValueAndValidity();
        }
    }

    private checkDates(inicio, fin) {
        const inicioD = new Date(inicio);
        const finD = new Date(fin);
        return inicioD <= finD;
    }

    public checkValuesForm(): boolean {
        return (
            this.filtersForm.get('fechaInicio').value ||
            this.filtersForm.get('fechaFin').value ||
            this.filtersForm.get('userName').value ||
            this.filtersForm.get('applicationRoleId').value ||
            this.filtersForm.get('origen').value ||
            this.filtersForm.get('tipoLogId').value
        ) ? true : false;
    }

    public filtrar() {
        this.validDates();

        if (this.filtersForm.valid) {
            this.applyFilters();
        }
    }

    public resetForm() {
        this.filtersForm.reset();
    }

    public applyFilters() {
        this.currentPage = 0;
        this.dropdownSection = -1;
        this._adminContext.logsFilters = {
            fechaInicio: this.filtersForm.get('fechaInicio').value ?
                this.utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaInicio').value) : '',
            fechaFin: this.filtersForm.get('fechaFin').value ?
                this.utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaFin').value) : '',
            userName: this.filtersForm.get('userName').value ? this.filtersForm.get('userName').value : '',
            applicationRoleId: this.filtersForm.get('applicationRoleId').value ? this.filtersForm.get('applicationRoleId').value.id : '',
            origen: this.filtersForm.get('origen').value ? this.filtersForm.get('origen').value : '',
            tipoLogId: this.filtersForm.get('tipoLogId').value ? this.filtersForm.get('tipoLogId').value.id : '',
            numPagina: this.currentPage,
            tamPagina: this.itemsPerPage
        };

        this.getLogs();
    }

    public getLogs() {
        if (this._adminContext.logsFilters) {
            this._filters = this._adminContext.logsFilters;
        } else {
            this.applyFilters();
        }
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._adminService.getLogs(this._filters).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.totalItems = resp.paginacionMeta.totalCount;
            this.logs = resp.lista;
        }, (error) => {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
        });
    }

    public getRoles() {
        this._spinnerService.showSpinner();
        this._adminService.getRoles().toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.roles = resp.map(el => ({ id: el.applicationRoleId, des: el.roleName }));
        });
    }

    public getTiposLog() {
        this._spinnerService.showSpinner();
        this._adminService.getTiposLog().toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.tiposLogs = resp;
        });
    }

    public showSection(i) {
        this.dropdownSection = this.dropdownSection === i ? -1 : i;
        this.pestanaActiva[i] = this.pestanaActiva[i] > 0 ? this.pestanaActiva[i] : 1;
        // Desplazamiento a la izquierda para tablet (mismo tamaño máx. que en table.scss)
        if (this.dropdownSection !== -1 && window.innerWidth <= 1199) {
            // Retardo en caso de que haya otro detalle abierto
            setTimeout(() => {
                document.querySelector('table')?.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'start'});
            }, 50);
        }
    }

    public pageChange(page) {
        this.dropdownSection = -1;
        this._adminContext.logsFilters.numPagina = this.currentPage = page - 1;
        this.getLogs();
    }

    public itemsPerPageChange(items) {
        this.dropdownSection = -1;
        this._adminContext.logsFilters.numPagina = this.currentPage = 0;
        this._adminContext.logsFilters.tamPagina = this.itemsPerPage = items;
        this.getLogs();
    }

    public getIcon(prop: string) {
        let iconClass = 'fa fa-sort';
        if (this._filters && this._filters.orderItems && this._filters.orderItems[0].orderProperty === prop) {
            iconClass = this._filters.orderItems[0].orderByDescending === true ? 'fa fa-sort-down' : 'fa fa-sort-up';
        }
        return iconClass;
    }

    public toOrderBy(columnFilter, idColumn) {
        this.columnId = idColumn;
        if (this.logs && this.logs.length > 0) {
            if (this._adminContext.logsFilters) {
                this._filters = this._adminContext.logsFilters;
                if (!this.orderBy || this.orderBy.includes('DESC')) {
                    this.orderBy = 'ASC' + idColumn;
                    this._filters.orderItems = [
                        {
                            orderProperty: columnFilter,
                            orderByDescending: false
                        }
                    ];
                } else if (this.orderBy.includes('ASC')) {
                    this.orderBy = 'DESC' + idColumn;
                    this._filters.orderItems = [
                        {
                            orderProperty: columnFilter,
                            orderByDescending: true
                        }
                    ];
                }
                this.getLogs();
            }
        }
    }

    public trackByLogId(index: number, log: { id: number }): number {
        return log.id;
    }
}
